import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Logo from "../../assets/logo_beaive.svg";

import {
  LoginContainer,
  LoginForm,
  Title,
  SubTitle,
  InputGroup,
  Label,
  Input,
  PasswordInput,
  PasswordWrapper,
  ToggleButton,
  Button,
  ErrorMessage,
  LogoWrapper,
  Img,
} from "./Login.styles";
import UnsupportedScreen from "./UnsupportedScreen";

interface FormData {
  email: string;
  password: string;
}

const EyeIcon = ({ visible }: { visible: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {visible ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  const [isSupportedView, setIsSupportedView] = useState(true);

  useEffect(() => {
    const checkView = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const isPortrait = height >= width;
      const isMobileOrTablet = width <= 1024;

      setIsSupportedView(isPortrait && isMobileOrTablet);
    };

    checkView();
    window.addEventListener("resize", checkView);
    window.addEventListener("orientationchange", checkView);

    return () => {
      window.removeEventListener("resize", checkView);
      window.removeEventListener("orientationchange", checkView);
    };
  }, []);

  if (!isSupportedView) {
    return <UnsupportedScreen />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const validateForm = (): boolean => {
    if (!formData.email || !formData.password) {
      setError("Email and password are required");
      return false;
    }
    if (!formData.email.includes("@")) {
      setError("Please enter a valid email address");
      return false;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const result = await login(formData);

      if (!result.success) {
        setError(result.error || "Invalid email or password");
        return;
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <LogoWrapper>
          <Img src={Logo} alt="Beaive Logo" />
        </LogoWrapper>

        <Title>Welcome Back</Title>
        <SubTitle>Enter your credentials to access your wellness dashboard</SubTitle>

        <InputGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="name@company.com"
            autoComplete="email"
            disabled={isLoading}
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="password">Password</Label>
          <PasswordWrapper>
            <PasswordInput
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={isLoading}
            />
            <ToggleButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon visible={showPassword} />
            </ToggleButton>
          </PasswordWrapper>
        </InputGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Signing in..." : "Sign in"}
        </Button>

        <div style={{
          marginTop: '0.5rem',
          fontSize: '0.875rem',
          color: '#64748b',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          By signing in, you agree to our{" "}
          <a
            href="https://beaive.com/terms"
            style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>{" "}
          &{" "}
          <a
            href="https://beaive.com/privacy"
            style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>.
        </div>
      </LoginForm>
    </LoginContainer>
  );
};

export default Login;
